export const trimUrlSearchParams = (params) => {
    if (typeof URLSearchParams === "undefined" || !(params instanceof URLSearchParams)) return params;
    const trimmed = new URLSearchParams();
    for (const [key, value] of params.entries()) {
        trimmed.append(key, typeof value === "string" ? value.trim() : value);
    }
    return trimmed;
};

/**
 * Recursively trims all string values in an object or array.
 * Preserves Files, Blobs, Dates, RegExps, etc.
 */
export const deepTrim = (value) => {
    if (typeof value === "string") {
        return value.trim();
    }
    if (Array.isArray(value)) {
        return value.map(deepTrim);
    }
    if (typeof URLSearchParams !== "undefined" && value instanceof URLSearchParams) {
        return trimUrlSearchParams(value);
    }
    if (
        value !== null &&
        typeof value === "object" &&
        !(value instanceof File) &&
        !(value instanceof Blob) &&
        !(value instanceof FormData) &&
        !(value instanceof Date) &&
        !(value instanceof RegExp)
    ) {
        const trimmed = {};
        for (const [k, v] of Object.entries(value)) {
            trimmed[k] = deepTrim(v);
        }
        return trimmed;
    }
    return value;
};

/**
 * Trims string values inside a FormData instance.
 */
export const trimFormData = (formData) => {
    if (!(formData instanceof FormData)) return formData;
    const trimmed = new FormData();
    for (const [key, value] of formData.entries()) {
        if (typeof value === "string") {
            trimmed.append(key, value.trim());
        } else {
            trimmed.append(key, value);
        }
    }
    return trimmed;
};

/**
 * Helper to trim request data and query params in Axios config.
 */
export const trimRequestConfig = (config) => {
    if (!config) return config;

    if (config.params) {
        if (typeof URLSearchParams !== "undefined" && config.params instanceof URLSearchParams) {
            config.params = trimUrlSearchParams(config.params);
        } else {
            config.params = deepTrim(config.params);
        }
    }

    if (config.data) {
        if (config.data instanceof FormData) {
            config.data = trimFormData(config.data);
        } else if (typeof config.data === "string") {
            try {
                const parsed = JSON.parse(config.data);
                config.data = JSON.stringify(deepTrim(parsed));
            } catch {
                config.data = config.data.trim();
            }
        } else {
            config.data = deepTrim(config.data);
        }
    }

    return config;
};

/**
 * Updates a DOM input/textarea value and triggers React's synthetic event handler.
 */
export const setNativeValue = (element, value) => {
    const valueSetter = Object.getOwnPropertyDescriptor(element, "value")?.set;
    const prototype = Object.getPrototypeOf(element);
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

    if (prototypeValueSetter && valueSetter !== prototypeValueSetter) {
        prototypeValueSetter.call(element, value);
    } else if (valueSetter) {
        valueSetter.call(element, value);
    } else {
        element.value = value;
    }
};

/**
 * Initializes global event listener to auto-trim inputs and textareas on focusout/blur.
 */
export const initGlobalInputTrimmer = () => {
    if (typeof window === "undefined" || typeof document === "undefined") return;

    document.addEventListener(
        "focusout",
        (e) => {
            const target = e.target;
            if (
                target &&
                (target.tagName === "INPUT" || target.tagName === "TEXTAREA") &&
                target.type !== "password" &&
                target.type !== "file" &&
                target.type !== "checkbox" &&
                target.type !== "radio" &&
                typeof target.value === "string"
            ) {
                const trimmed = target.value.trim();
                if (trimmed !== target.value) {
                    setNativeValue(target, trimmed);
                    target.dispatchEvent(new Event("input", { bubbles: true }));
                    target.dispatchEvent(new Event("change", { bubbles: true }));
                }
            }
        },
        true
    );
};
